import { useCallback } from "react";
import { invalidateInvoiceQueries } from "@api/invoice.api";
import { useDeleteScheduleApplicationMutation } from "@api/scheduleTemplate.api";
import { ScheduleDeleteJournalEntryMode } from "@common/types/domains/accounting/schedule";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    ShowLockedPeriods,
    useScheduleLockedPeriodsDialog,
} from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";

/**
 * Single removal call site for both invoice Schedule columns.
 *
 * This is the lifecycle `delete` verb, not the bulk unapply it replaced:
 * unapply refuses outright once any journal entry exists, and apply posts the
 * whole ladder, so on an applied schedule it could never succeed.
 */
export function useRemoveInvoiceSchedule({
    onRemoved,
    showLockedPeriods: showLocalLockedPeriods,
}: {
    onRemoved?: () => Promise<void> | void;
    showLockedPeriods?: ShowLockedPeriods;
} = {}) {
    const { error } = useToaster();
    const mutation = useDeleteScheduleApplicationMutation();
    const showLockedPeriods = useScheduleLockedPeriodsDialog(
        showLocalLockedPeriods,
    );

    const removeSchedule = useCallback(
        async (options: {
            entityId: number;
            scheduleApplicationId: string;
            journalEntryMode: ScheduleDeleteJournalEntryMode;
        }): Promise<boolean> => {
            try {
                await mutation.mutateAsync({
                    applicationId: options.scheduleApplicationId,
                    entityId: options.entityId,
                    journalEntryMode: options.journalEntryMode,
                });
                await (onRemoved ?? invalidateInvoiceQueries)();
                return true;
            } catch (removeError) {
                if (!showLockedPeriods(removeError)) {
                    error(
                        removeError instanceof Error
                            ? removeError.message
                            : "Failed to remove schedule",
                    );
                }
                return false;
            }
        },
        [error, mutation, onRemoved, showLockedPeriods],
    );

    return { removeSchedule, isRemoving: mutation.isPending };
}
